import { useCurrentCloudProvider } from '@labrav/common'
import React, { useMemo } from 'react'
import { HelpDrawerWithAccordion } from '@labrav/common'
import { notificationsImages } from '../../../common/utils/productCreationUtil'
import {
  FaqQuestion,
  FlyoutProductCreationFaqSection,
  HelpDrawerFaqItems,
} from '../FlyoutProductFormHelpFaqs/ProductCreationFaqUtil'
import { CarouselWithModal } from '../../../common/components/CarouselWithModal/CarouselWithModal'
import { CloudType } from '../../../types/index'

const awsFaqItems: HelpDrawerFaqItems[] = [
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.NOTIFICATIONS_BUYER_NOTIFICATIONS
        }
        question="What happens if I disable buyer emails via Labra?"
      />
    ),
    answer: (
      <div>
        If you disable this feature, Labra will no longer send automated emails
        to your buyers. Instead, you will need to manage and send all buyer
        notifications manually. AWS requires sellers to communicate key events
        like contract activation and renewal reminders. If you don’t have an
        in-house notification system, we recommend keeping this feature enabled
        to ensure buyers receive timely updates.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.NOTIFICATIONS_BUYER_NOTIFICATIONS
        }
        question="What buyer events require communication?"
      />
    ),
    answer: (
      <div>
        <p>As a seller, you need to inform buyers about key events such as:</p>
        <ul
          style={{
            paddingLeft: '20px',
            listStyleType: 'disc',
            margin: 'revert',
          }}
        >
          <li>Contract activation after a purchase</li>
          <li>Private offer expirations or renewals</li>
          <li>Important subscription updates</li>
        </ul>
        <p>
          Labra automates these notifications for you, ensuring buyers receive
          timely and consistent communication.
        </p>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.NOTIFICATIONS_BUYER_NOTIFICATIONS
        }
        question="Can I customize the emails Labra sends to my buyers?"
      />
    ),
    answer: (
      <div>
        Yes, you can personalize buyer emails by adding a custom message and
        your company logo, ensuring the communication aligns with your brand.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.NOTIFICATIONS_BUYER_NOTIFICATIONS
        }
        question="What events trigger notifications from Labra?"
      />
    ),
    answer: (
      <div>
        <p>Labra notifies you about key marketplace events, including:</p>
        <ul
          style={{
            paddingLeft: '20px',
            listStyleType: 'disc',
            margin: 'revert',
          }}
        >
          <li>
            Buyer lifecycle updates (new contracts, renewals, cancellations,
            upgrades)
          </li>
          <li>Listing status changes</li>
          <li>Private offer activity</li>
          <li>Reseller authorizations</li>
          <li>Metering and usage updates</li>
        </ul>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.NOTIFICATIONS_BUYER_NOTIFICATIONS
        }
        question="Who on my team will receive these email notifications?"
      />
    ),
    answer: (
      <div>
        Only the team members whose email addresses you specify in the
        notification settings will receive emails for selected events.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.NOTIFICATIONS_BUYER_NOTIFICATIONS
        }
        question="Can I receive notifications on Slack as well?"
      />
    ),
    answer: (
      <div>
        Yes, you can enable Slack notifications from{' '}
        <strong>Settings &gt; Notifications</strong> within the Labra app to get
        real-time updates.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.NOTIFICATIONS_BUYER_NOTIFICATIONS
        }
        question=" What happens if I disable the emails to buyers via Labra?"
      />
    ),
    answer: (
      <div>
        Labra will no longer send any mail to your buyers. All email
        notifications will have to be handled by you (seller).
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.NOTIFICATIONS_BUYER_NOTIFICATIONS
        }
        question="If I disable emails to buyers then how will they come to know if their subscription has started and regarding other events?"
      />
    ),
    answer: (
      <div>
        As the seller you will need to communicate with your buyers and inform
        them when their subscription starts, when it’s about to end, etc.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.NOTIFICATIONS_BUYER_NOTIFICATIONS
        }
        question="Does AWS not send emails to my buyers to confirm their purchase of any product?"
      />
    ),
    answer: (
      <div>
        No, AWS does not communicate with your buyer at any point. The
        communication will have to be handled by the seller (you).
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.NOTIFICATIONS_BUYER_NOTIFICATIONS
        }
        question="Where will the customized logo be shown?"
      />
    ),
    answer: (
      <div>
        The logo you add will act as the email banner (refer to number 1 in
        third image added at the top of the page) only in those emails which
        Labra sends to your buyers. This will help your buyer quickly identify
        such emails.
      </div>
    ),
  },
]

const azureFaqItems: HelpDrawerFaqItems[] = [
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.NOTIFICATIONS_BUYER_NOTIFICATIONS
        }
        question="What happens if I disable buyer emails via this platform?"
      />
    ),
    answer: (
      <div>
        If you disable this feature, Platform will no longer send automated
        emails to your buyers. Instead, you will need to manage and send all
        buyer notifications manually. If you don’t have an in-house notification
        system, we recommend keeping this feature enabled to ensure buyers
        receive timely updates.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.NOTIFICATIONS_BUYER_NOTIFICATIONS
        }
        question="What buyer events require communication?"
      />
    ),
    answer: (
      <div>
        <p>As a seller, you need to inform buyers about key events such as:</p>
        <ul
          style={{
            paddingLeft: '20px',
            listStyleType: 'disc',
            margin: 'revert',
          }}
        >
          <li>Contract activation after a purchase</li>
          <li>Private offer expirations or renewals</li>
          <li>Important subscription updates</li>
        </ul>
        <p>
          We automate these notifications for you, ensuring buyers receive
          timely and consistent communication.
        </p>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.NOTIFICATIONS_BUYER_NOTIFICATIONS
        }
        question="Can I customize the emails Platform sends to my buyers?"
      />
    ),
    answer: (
      <div>
        Yes, you can personalize buyer emails by adding a custom message and
        your company logo, ensuring the communication aligns with your brand.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.NOTIFICATIONS_BUYER_NOTIFICATIONS
        }
        question="What events trigger notifications from Platform?"
      />
    ),
    answer: (
      <div>
        <p>Platform notifies you about key marketplace events, including:</p>
        <ul
          style={{
            paddingLeft: '20px',
            listStyleType: 'disc',
            margin: 'revert',
          }}
        >
          <li>
            Buyer lifecycle updates (new contracts, renewals, cancellations,
            upgrades)
          </li>
          <li>Listing status changes</li>
          <li>Private offer activity</li>
          <li>Reseller authorizations</li>
          <li>Metering and usage updates</li>
        </ul>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.NOTIFICATIONS_BUYER_NOTIFICATIONS
        }
        question="Who on my team will receive these email notifications?"
      />
    ),
    answer: (
      <div>
        Only the team members whose email addresses you specify in the
        notification settings will receive emails for selected events.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.NOTIFICATIONS_BUYER_NOTIFICATIONS
        }
        question="If I disable emails to buyers then how will they come to know if their subscription has started and regarding other events?"
      />
    ),
    answer: (
      <div>
        As the seller you will need to communicate with your buyers and inform
        them when their subscription starts, when it’s about to end, etc.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.NOTIFICATIONS_BUYER_NOTIFICATIONS
        }
        question="Where will the customized logo be shown?"
      />
    ),
    answer: (
      <div>
        The logo you add will act as the email banner (refer to number 1 in
        third image added at the top of the page) only in those emails which
        this platform sends to your buyers. This will help your buyer quickly
        identify such emails.
      </div>
    ),
  },
]

interface FlyoutProductNotificationSettingsFaqsProps {
  indexModal: number
  setIndexModal: (index: number) => void
}

export const FlyoutProductNotificationSettingsFaqs: React.FC<
  FlyoutProductNotificationSettingsFaqsProps
> = ({ indexModal, setIndexModal }) => {
  const selectedCloud = useCurrentCloudProvider()
  const faqItems = useMemo(() => {
    return selectedCloud === CloudType.AWS ? awsFaqItems : azureFaqItems
  }, [selectedCloud])
  return (
    <HelpDrawerWithAccordion
      headerTitle="Help & FAQs - Notifications Settings"
      faqItems={faqItems}
      customContent={
        <CarouselWithModal
          carouselData={notificationsImages}
          setCurrentIndex={setIndexModal}
          currentIndex={indexModal}
        />
      }
    />
  )
}
